import React, { useState, useCallback, useRef, useEffect } from 'react';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import TourItem from './tour-item';
import Icon from '../icon';
import Button from '../button';
import Swipe from '../swipe';
import { PanelLeft, PanelRight } from '../panel';
import { SWIPE_TYPE_END } from './constants/common';
import verifiers from './verifiers';
import helpers from './helpers';
import noop from '../utils/noop';

function Tour({
  items,
  captions,
  className = '',
  handleCloseDialog,
  handleFinishTour = noop,
  showPrevButton = true,
  showNextButton = true,
  showFinishButton = true,
}) {
  const [step, setStep] = useState(0);
  const isFirstStep = step === 0;
  const isLastStep = items.length - 1 === step;
  const swipeRef = useRef({});

  const handlePrev = useCallback(() => {
    setStep(step - 1);
  }, [step]);

  const handleNext = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  const handleSwipe = useCallback(
    (event) => {
      // @ts-ignore TS(2339): Property 'touchstartX' does not exist on type '{}'... Remove this comment to see the full error message
      const { touchstartX, touchstartY } = swipeRef.current;
      const touchData = {
        touchstartX,
        touchstartY,
        touchendY: event.changedTouches[0].screenY,
        touchendX: event.changedTouches[0].screenX,
      };

      const normalizedTouchData = helpers.normalizeTouchData(touchData);
      const isSwipeDetected = verifiers.isSwipeDetected(normalizedTouchData);
      const isDirectionLeft = verifiers.isSwipeDirectionLeft(normalizedTouchData);

      if (isSwipeDetected && isDirectionLeft && !isLastStep) handleNext();
      if (isSwipeDetected && !isDirectionLeft && !isFirstStep) handlePrev();
    },
    [isFirstStep, isLastStep, handlePrev, handleNext]
  );

  const handleSwitching = useCallback((index, type) => {
    if (type !== SWIPE_TYPE_END) return;
    setStep(index);
  }, []);

  const updateTouchstartX = useCallback((event) => {
    swipeRef.current.touchstartX = event.changedTouches[0].screenX;
    swipeRef.current.touchstartY = event.changedTouches[0].screenY;
  }, []);

  const isMobile = bowser.mobile;
  const isTablet = bowser.tablet;
  const isTouchDevice = isMobile || isTablet;
  const [customItemClassNames, setCustomItemClassNames] = useState({});
  useEffect(() => {
    const currentItem = items.find((_, index) => step === index);
    if (currentItem && currentItem.rootClassName) {
      setCustomItemClassNames({ [currentItem.rootClassName]: true });
      return;
    }
    setCustomItemClassNames({});
  }, [items, step]);
  const rootClassNames = classNames('ws-tour', className, {
    'ws-tour--mobile': isMobile,
    ...customItemClassNames,
  });

  // @ts-ignore TS(2339): Property 'skip' does not exist on type '{ skip?: s... Remove this comment to see the full error message
  const { skip, next, back, finish } = captions.buttons;
  const buttonSize = isTouchDevice ? 'medium' : 'large';

  return (
    <div onTouchStart={updateTouchstartX} onTouchEnd={handleSwipe} className={rootClassNames}>
      <div className="ws-tour__skip" onClick={() => handleCloseDialog(step)}>
        {skip}
      </div>
      <Swipe
        index={step}
        enable={isTouchDevice}
        onSwitching={handleSwitching}
        className="ws-tour__screen"
      >
        {items.map((item, index) => (
          // eslint-disable-next-line yola/react/no-array-index-key
          <TourItem {...item} key={`tour-item-${item.type}-${index}`} />
        ))}
      </Swipe>
      <div className="ws-tour__controls">
        <PanelLeft>
          {!isFirstStep && showPrevButton && (
            <Button type="default" onClick={handlePrev} size={buttonSize}>
              <Icon size="22" glyph="chevron-left" strokeWidth="1.3" />
              {back}
            </Button>
          )}
        </PanelLeft>
        <div className="ws-tour__controls-dots">
          {items.map((item, index) => (
            <div
              // eslint-disable-next-line yola/react/no-array-index-key
              key={`dot-${item.type}-${index}`}
              className={classNames('ws-tour__controls-dots-item', {
                'ws-tour__controls-dots-item--active': step === index,
              })}
            />
          ))}
        </div>
        <PanelRight>
          {!isLastStep && showNextButton && (
            <Button type="primary" onClick={handleNext} size={buttonSize}>
              {next}
              <Icon size="22" glyph="chevron-right" strokeWidth="1.3" />
            </Button>
          )}
          {showFinishButton && isLastStep && finish && (
            <Button type="primary" onClick={handleFinishTour} size={buttonSize}>
              {finish}
            </Button>
          )}
        </PanelRight>
      </div>
    </div>
  );
}

export default Tour;
